'use client'

import { useEffect, useRef, useState } from 'react'
import { CALLOUT_MS, calloutForMarks, type CalloutView } from './calloutState'
import type { PuttMark } from './tileViewModel'

/**
 * The callout for the putt that just became visible, for `CALLOUT_MS` after it
 * did; null in between. A one-shot timer, not the board's 1 Hz tick, so the
 * pill leaves on time rather than on the next tick. Cleared when the round
 * changes or once the settlement is on screen.
 */
export function useBirdieCallout(input: {
  marks: readonly PuttMark[]
  raceId: string | undefined
  settlementVisible: boolean
}): CalloutView | null {
  const { marks, raceId, settlementVisible } = input
  const [callout, setCallout] = useState<CalloutView | null>(null)
  const shownRef = useRef(0)
  const raceRef = useRef(raceId)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => clearTimer, [])

  useEffect(() => {
    if (raceRef.current !== raceId) {
      raceRef.current = raceId
      shownRef.current = 0
      clearTimer()
      setCallout(null)
    }
  }, [raceId])

  useEffect(() => {
    if (settlementVisible) {
      clearTimer()
      setCallout(null)
      return
    }
    // `marks` is a fresh array every tick; only a LONGER one is a new putt.
    if (marks.length <= shownRef.current) return
    shownRef.current = marks.length
    const next = calloutForMarks(marks) ?? null
    clearTimer()
    setCallout(next)
    if (next !== null) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        setCallout(null)
      }, CALLOUT_MS)
    }
  }, [marks, settlementVisible])

  return callout
}
